import React, { useCallback, useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { SettingOutlined } from '@ant-design/icons';
import { Button, Checkbox, Dropdown, Input } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import dayjs from 'dayjs';

import AssetInventoryV2Context from '@totem/components/services/assetInventory/v2/AssetInventoryV2Context';
import {
  FINDING_LABELS,
  formatCalendarDay,
  formatCalendarMonth,
  formatDay,
  INFORMATIONAL_FINDINGS,
  RED_FINDINGS,
} from '@totem/components/services/assetInventory/v2/checks';
import { getSystemTypeLabel } from '@totem/components/services/assetInventory/v2/configuration';
import { AssetInventoryDevice } from '@totem/components/services/assetInventory/v2/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { debounce } from '@totem/utilities/debounce';

import './assetInventoryV2.css';

const DEBOUNCE_TIME = 300;

const MAC_NAME = /^([0-9a-f]{2}[:-]){5}[0-9a-f]{2}/i;

const OPTIONAL_COLUMNS = [
  { key: 'lastBackup', label: 'Last backup', defaultOn: true },
  { key: 'serviceStatus', label: 'Service contract', defaultOn: true },
  { key: 'operatingSystem', label: 'Operating system', defaultOn: false },
  { key: 'firmware', label: 'Firmware', defaultOn: false },
  { key: 'region', label: 'Region', defaultOn: false },
  { key: 'maintenanceWindows', label: 'Maintenance window', defaultOn: false },
];

// Devices discovered only by MAC address get a readable label; the raw name stays underneath.
const readableName = (device: AssetInventoryDevice) =>
  MAC_NAME.test(device.name)
    ? [
        [device.vendor, device.type.toLowerCase()].filter(Boolean).join(' ') ||
          'Unnamed device',
        device.ipAddresses[0],
      ]
        .filter(Boolean)
        .join(' · ')
    : null;

const supportCell = (device: AssetInventoryDevice) => {
  if (device.pastEndOfSupport) {
    return <span styleName="pill pill-red">Past support</span>;
  }
  if (device.findings.includes('endOfSupportNotMarked')) {
    return (
      <span styleName="pill pill-amber">
        Ended {formatCalendarMonth(device.endOfSupport)}
      </span>
    );
  }
  if (device.findings.includes('endOfSupportSoon')) {
    return (
      <span styleName="pill pill-amber">
        Ends {formatCalendarDay(device.endOfSupport)}
      </span>
    );
  }
  return <span styleName="sub">—</span>;
};

const serviceCell = (device: AssetInventoryDevice) => {
  switch (device.serviceStatus) {
    case 'expired':
      return <span styleName="pill pill-red">Expired</span>;
    case 'ending':
      return (
        <span styleName="pill pill-amber">
          Ends {formatCalendarDay(device.serviceEnd)}
        </span>
      );
    case 'active':
      return <span styleName="pill pill-green">Active</span>;
    default:
      return <span styleName="pill pill-grey">None on file</span>;
  }
};

const backupCell = (device: AssetInventoryDevice) => {
  if (!device.isComputer) {
    return <span styleName="sub">Not applicable</span>;
  }
  if (!device.lastBackup) {
    return '—';
  }
  return dayjs(device.lastBackup).isSame(dayjs(), 'day')
    ? 'Today'
    : formatDay(device.lastBackup);
};

const DetailsTab = () => {
  const { summary, devices, devicesLoading, totalDevices } = useContext(
    AssetInventoryV2Context,
  );
  const {
    input,
    handleFilter,
    handleTableChange,
    getFilterValue,
    getFilterValues,
  } = useContext(InputContext);

  const [searchTerm, setSearchTerm] = useState<string>(
    getFilterValue('search'),
  );
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    OPTIONAL_COLUMNS.filter((column) => column.defaultOn).map(
      (column) => column.key,
    ),
  );

  const sendSearch = useCallback(
    debounce(
      (newSearchTerm: string) => handleFilter('search', newSearchTerm),
      DEBOUNCE_TIME,
    ),
    [],
  );

  const activeFinding = getFilterValues('finding')[0] ?? 'all';
  const totals = summary?.totals;

  const quickFilters = [
    { key: 'all', label: `All ${totals?.assets ?? ''}`.trim() },
    { key: 'any', label: 'Has findings' },
    {
      key: 'notManaged',
      label: totals
        ? `Not managed · ${totals.assets - totals.managed}`
        : 'Not managed',
    },
    {
      key: 'pastEndOfSupport',
      label: totals
        ? `Past support · ${totals.pastEndOfSupport}`
        : 'Past support',
    },
    { key: 'backupIssue', label: 'Backup issue' },
    { key: 'noServiceContract', label: 'No service contract' },
  ];
  const isQuickFilter = quickFilters.some(
    (filter) => filter.key === activeFinding,
  );

  const sortOrder = (key: string) =>
    input.sort.field === key
      ? (input.sort.direction as 'ascend' | 'descend')
      : null;

  const optionalColumns: Record<string, ColumnProps<AssetInventoryDevice>> = {
    lastBackup: {
      title: 'Last backup',
      key: 'lastBackup',
      sorter: true,
      sortOrder: sortOrder('lastBackup'),
      render: (_, device) => backupCell(device),
    },
    serviceStatus: {
      title: 'Service',
      key: 'serviceStatus',
      sorter: true,
      sortOrder: sortOrder('serviceStatus'),
      render: (_, device) => serviceCell(device),
    },
    operatingSystem: {
      title: 'OS',
      key: 'operatingSystem',
      sorter: true,
      sortOrder: sortOrder('operatingSystem'),
      render: (_, device) => device.operatingSystem || '—',
    },
    firmware: {
      title: 'Firmware',
      key: 'firmware',
      sorter: true,
      sortOrder: sortOrder('firmware'),
      render: (_, device) => device.firmware || '—',
    },
    region: {
      title: 'Region',
      key: 'region',
      sorter: true,
      sortOrder: sortOrder('region'),
      render: (_, device) => device.region?.name ?? '—',
    },
    maintenanceWindows: {
      title: 'Maint. window',
      key: 'maintenanceWindows',
      render: (_, device) =>
        device.maintenanceWindows > 0 ? (
          'Agreed'
        ) : (
          <span styleName="sub">None</span>
        ),
    },
  };

  const columns: ColumnProps<AssetInventoryDevice>[] = [
    {
      title: 'Name',
      key: 'name',
      sorter: true,
      sortOrder: sortOrder('name'),
      render: (_, device) => {
        const readable = readableName(device);
        return (
          <>
            <Link
              styleName="device-name"
              to={`/dashboard/devices/${device.id}`}
            >
              {readable ?? device.name}
            </Link>
            {readable && <div styleName="raw-name">{device.name}</div>}
          </>
        );
      },
    },
    {
      title: 'Type',
      key: 'type',
      sorter: true,
      sortOrder: sortOrder('type'),
      render: (_, device) => device.type || '—',
    },
    {
      title: 'Vendor',
      key: 'vendor',
      sorter: true,
      sortOrder: sortOrder('vendor'),
      render: (_, device) => device.vendor || '—',
    },
    {
      title: 'Building · system',
      key: 'building',
      sorter: true,
      sortOrder: sortOrder('building'),
      render: (_, device) => (
        <>
          {device.building ? (
            <Link to={`/dashboard/buildings/${device.building.id}`}>
              {device.building.name}
            </Link>
          ) : (
            '—'
          )}
          {device.controlSystem && (
            <div styleName="sub">
              <Link to={`/dashboard/controlsystems/${device.controlSystem.id}`}>
                {device.systemType
                  ? getSystemTypeLabel(device.systemType)
                  : device.controlSystem.name}
              </Link>
            </div>
          )}
        </>
      ),
    },
    {
      title: 'IP',
      key: 'ipAddresses',
      render: (_, device) =>
        device.ipAddresses.length > 0
          ? device.ipAddresses.map((address) => (
              <div key={address}>{address}</div>
            ))
          : '—',
    },
    {
      title: 'Mgmt',
      key: 'isManaged',
      sorter: true,
      sortOrder: sortOrder('isManaged'),
      render: (_, device) => (
        <span
          styleName={device.isManaged ? 'pill pill-green' : 'pill pill-grey'}
        >
          {device.isManaged ? 'Managed' : 'Not managed'}
        </span>
      ),
    },
    ...OPTIONAL_COLUMNS.filter(
      (column) =>
        column.key !== 'serviceStatus' && visibleColumns.includes(column.key),
    ).map((column) => optionalColumns[column.key]),
    {
      title: 'Support',
      key: 'endOfSupport',
      sorter: true,
      sortOrder: sortOrder('endOfSupport'),
      render: (_, device) => supportCell(device),
    },
    ...(visibleColumns.includes('serviceStatus')
      ? [optionalColumns.serviceStatus]
      : []),
    {
      title: 'Findings',
      key: 'findings',
      render: (_, device) => {
        const findings = device.findings.filter(
          (finding) => !INFORMATIONAL_FINDINGS.includes(finding),
        );
        return findings.length > 0 ? (
          <div styleName="flags">
            {findings.map((finding) => (
              <span
                key={finding}
                styleName={
                  RED_FINDINGS.includes(finding)
                    ? 'pill pill-red'
                    : 'pill pill-amber'
                }
              >
                {FINDING_LABELS[finding] ?? finding}
              </span>
            ))}
          </div>
        ) : (
          <span styleName="sub">None</span>
        );
      },
    },
  ];

  return (
    <div styleName="pane">
      <div styleName="details-toolbar">
        <Input
          allowClear
          styleName="search"
          placeholder="Search name, IP address, vendor, building or system"
          value={searchTerm}
          onChange={(event) => {
            setSearchTerm(event.target.value);

            sendSearch(event.target.value);
          }}
        />
        {quickFilters.map((filter) => (
          <button
            key={filter.key}
            type="button"
            styleName={activeFinding === filter.key ? 'chip chip-on' : 'chip'}
            onClick={() =>
              handleFilter('finding', filter.key === 'all' ? [] : [filter.key])
            }
          >
            {filter.label}
          </button>
        ))}
        {!isQuickFilter && (
          <button
            type="button"
            styleName="chip chip-on"
            onClick={() => handleFilter('finding', [])}
          >
            {FINDING_LABELS[activeFinding] ?? activeFinding} ✕
          </button>
        )}
        <span styleName="column-picker">
          <Dropdown
            trigger={['click']}
            popupRender={() => (
              <div
                style={{
                  background: 'var(--ant-color-bg-elevated)',
                  boxShadow: 'var(--ant-box-shadow-secondary)',
                  borderRadius: 6,
                  padding: '8px 12px',
                }}
              >
                <Checkbox.Group
                  style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                  value={visibleColumns}
                  onChange={(values) => setVisibleColumns(values as string[])}
                  options={OPTIONAL_COLUMNS.map((column) => ({
                    label: column.label,
                    value: column.key,
                  }))}
                />
              </div>
            )}
          >
            <Button icon={<SettingOutlined />}>Columns</Button>
          </Dropdown>
        </span>
      </div>
      <IBTable
        size="small"
        columns={columns}
        dataSource={devices}
        loading={devicesLoading}
        rowKey={(device) => device.id}
        scroll={{ x: 'max-content' }}
        onChange={(pagination, filters, sorter, extra) =>
          handleTableChange(pagination, filters, sorter, extra)
        }
        pagination={{
          current: input.pagination.page,
          pageSize: input.pagination.pageSize,
          total: totalDevices,
          showSizeChanger: true,
          showTotal: (total) => `${total} asset${total === 1 ? '' : 's'}`,
        }}
      />
    </div>
  );
};

export default DetailsTab;
